// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/hooks/useHomeWeather.ts
// 🔧 PURPOSE: Il meteo della Home, senza il resto di Esplora (treni,
//             planner, wallet, posizione):
//             - useHomeWeather: adesso a Mola, temperatura ed etichetta, e
//               la temperatura del mare ("26° · mare 24°" sulla card del
//               soggiorno). Stessa funzione e stessa cache di 30 minuti di
//               Esplora (loadWeather), cosi' le due pagine dicono lo stesso.
//             - useCheckInForecast: la previsione per il giorno di arrivo,
//               quando mancano al massimo 14 giorni (oltre, open-meteo non
//               ha niente di serio). Cache di 3 ore per giorno di arrivo.
//             Stesse coordinate di Esplora. Se la rete non risponde il
//             valore resta null e la card si mostra senza meteo.
// ============================================

import { useEffect, useState } from 'react';
import {
  loadWeather,
  MOLA_LAT,
  MOLA_LON,
  weatherLabelKey,
  type WeatherSummary,
} from './useExploreLive';
import { dayKeyDiff, romeDayKey } from '../lib/romeDay';

/** Oltre due settimane la previsione non si mostra. */
export const FORECAST_MAX_DAYS = 14;
const FORECAST_CACHE_KEY = 'nva-home-forecast';
const FORECAST_TTL_MS = 3 * 60 * 60 * 1000;

/** Il meteo di adesso (null finché non arriva, o se la rete non risponde). */
export function useHomeWeather(enabled = true): WeatherSummary | null {
  const [weather, setWeather] = useState<WeatherSummary | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => {
      loadWeather().then((value) => { if (alive && value) setWeather(value); });
    };
    load();
    // Tornando sull'app: dalla cache se ha meno di 30 minuti, se no dalla rete.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);

  return enabled ? weather : null;
}

export interface CheckInForecast {
  /** Il giorno di arrivo ("AAAA-MM-GG", Roma). */
  day: string;
  /** Codice WMO. */
  code: number;
  /** Massima del giorno, arrotondata. */
  tempMax: number;
  /** Chiave sotto `weather.*` (clearSky, rain, ...), come in Esplora. */
  labelKey: string;
}

function readForecastCache(day: string): CheckInForecast | null {
  try {
    const raw = localStorage.getItem(FORECAST_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { fetchedAt?: number; data?: CheckInForecast };
    if (!parsed?.data || typeof parsed.fetchedAt !== 'number') return null;
    if (parsed.data.day !== day || Date.now() - parsed.fetchedAt > FORECAST_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeForecastCache(data: CheckInForecast): void {
  try {
    localStorage.setItem(FORECAST_CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), data }));
  } catch {
    /* storage pieno o bloccato: la previsione si ripesca la prossima volta */
  }
}

/** La previsione per `day` ("AAAA-MM-GG"), se cade fra oggi e 14 giorni. */
export async function loadCheckInForecast(day: string, now: Date = new Date()): Promise<CheckInForecast | null> {
  const days = dayKeyDiff(day, romeDayKey(now));
  if (days === null || days < 0 || days > FORECAST_MAX_DAYS) return null;
  const cached = readForecastCache(day);
  if (cached) return cached;
  try {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${MOLA_LAT}&longitude=${MOLA_LON}` +
        `&daily=weather_code,temperature_2m_max&timezone=Europe%2FRome&forecast_days=${days + 1}`
    );
    if (!res.ok) return null;
    const json = await res.json();
    const daily = json?.daily;
    const index = Array.isArray(daily?.time) ? daily.time.indexOf(day) : -1;
    if (index < 0) return null;
    const code = daily.weather_code?.[index];
    const temp = daily.temperature_2m_max?.[index];
    // null = giorno non ancora coperto dal modello: niente previsione.
    if (typeof code !== 'number' || typeof temp !== 'number') return null;
    const forecast: CheckInForecast = {
      day,
      code,
      tempMax: Math.round(temp),
      labelKey: weatherLabelKey(code),
    };
    writeForecastCache(forecast);
    return forecast;
  } catch {
    return null;
  }
}

/**
 * La previsione per il giorno di arrivo (chiave "AAAA-MM-GG" di Roma, per
 * esempio HomeState.checkInDay). null oltre 14 giorni, prima che arrivi o
 * senza rete.
 */
export function useCheckInForecast(checkInDay: string | null | undefined, enabled = true): CheckInForecast | null {
  const [forecast, setForecast] = useState<CheckInForecast | null>(null);

  useEffect(() => {
    setForecast(null);
    if (!enabled || !checkInDay) return;
    let alive = true;
    loadCheckInForecast(checkInDay).then((value) => { if (alive) setForecast(value); });
    return () => { alive = false; };
  }, [checkInDay, enabled]);

  return enabled && forecast && forecast.day === checkInDay ? forecast : null;
}
